// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
// import { attributesProps } from "@store/Themes/common/props/attributes"; // Store Custom Code
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";

/* Store Custom Code - Start */
import { ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import ProductAdditionalInfos from "@store/Library/Components/ProductShipsFrom";
import { attributesProps } from "@store/Themes/common/props/attributes";
import { useCurrentPage } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import constants from "@store/constants";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";

const upperCasedLabelProps = mergeProps<TypographyPresentationProps>(attributesProps.label, {
    css: css`
        text-transform: uppercase;
    `,
});

const lineNumberValueTextProps = mergeProps<TypographyPresentationProps>(attributesProps.value, {
    css: css`
        font-weight: bold;
    `,
});
/* Store Custom Code - End */

type PartNumber = string | JSX.Element;
const hasValue = (value?: PartNumber) => {
    return value && (typeof value === "string" || (value as JSX.Element).props?.children);
};
/* Starter Custom Code - End */

interface OwnProps {
    productNumber?: PartNumber; // Store Custom Code
    customerProductNumber?: PartNumber; // Starter Custom Code
    manufacturerItem?: PartNumber; // Starter Custom Code
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
    showProductNumber?: boolean;
    // Starter Custom Code
    overridedStyles?: ProductPartNumbersStyles;
    // Store Custom Code
    showLineNumber?: boolean;
    cartLineNumber?: string | number | null;
    showAdditionalInfo?: boolean;
    index?: number | null;
    pricing?: ProductPriceDto | null;
    additionalCustomerProductNumberCount?: number;
}

type Props = OwnProps;

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: GridContainerProps;
    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameGridItem?: GridItemProps;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: GridItemProps;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
    // Starter Custom Code
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    // Store Custom Code
    lineNumberLabelText?: TypographyPresentationProps;
    lineNumberValueText?: TypographyPresentationProps;
    count?: TypographyPresentationProps;
}

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    erpNumberGridItem: attributesProps.item,
    erpNumberLabelText: upperCasedLabelProps, // Store Custom Code
    erpNumberValueText: attributesProps.value,
    customerNameGridItem: attributesProps.item,
    customerNameLabelText: upperCasedLabelProps, // Store Custom Code
    customerNameValueText: attributesProps.value,
    manufacturerItemGridItem: attributesProps.item,
    manufacturerItemLabelText: upperCasedLabelProps, // Store Custom Code
    manufacturerItemValueText: attributesProps.value,
    wrapper: attributesProps.wrapper,
    innerWrapper: attributesProps.innerWrapper,
    // Store Custom Code
    lineNumberLabelText: lineNumberValueTextProps,
    lineNumberValueText: lineNumberValueTextProps,
    count: attributesProps.count,
};

const ProductPartNumbers: FC<Props> = ({
    productNumber,
    customerProductNumber,
    manufacturerItem,
    showCustomerName = true,
    showManufacturerItem = true,
    showLabel = true,
    extendedStyles,
    showProductNumber = true,
    // Starter Custom Code
    overridedStyles,
    // Store Custom Code
    showLineNumber = false,
    cartLineNumber,
    showAdditionalInfo = false,
    index,
    pricing,
    additionalCustomerProductNumberCount,
}) => {
    const styles = useExpandProps(productPartNumbersStyles, extendedStyles, overridedStyles); // Starter Custom Code
    const displayManufacturerNumber = showManufacturerItem && hasValue(manufacturerItem); // Starter Custom Code
    const displayCustomerNumber = showCustomerName && hasValue(customerProductNumber); // Starter Custom Code
    // Starter Removed - numberOfItems
    // Starter Removed - mobileGridItemWidth
    // Starter Removed - desktopGridItemWidth
    // Starter Removed - gridItemWidth

    /* Store Custom Code - Start */
    const { type: pageType } = useCurrentPage();
    const isOrderConfirmationPage = pageType === constants.pageType.orderConfirmation;
    const isReviewAndSubmitDetailsPage = pageType === constants.pageType.checkoutReviewAndSubmit;
    const isOrderDetailsPage = pageType === constants.pageType.orderDetails;
    const isOrderComplaintPage = pageType === constants.pageType.orderComplaint;
    const isOrderChangeRequest = pageType === constants.pageType.orderChangeRequest;
    const isReviewOrderChangeRequest = pageType === constants.pageType.orderChangeRequestReview;

    const displayLineNumber = showLineNumber && cartLineNumber;

    const isLineNumberMultiplied =
        isOrderConfirmationPage ||
        isReviewAndSubmitDetailsPage ||
        isOrderDetailsPage ||
        isOrderComplaintPage ||
        isOrderChangeRequest ||
        isReviewOrderChangeRequest;

    const lineToLineNumber = (line: number | string) => {
        const _line = Number(line);
        return (Number.isNaN(_line) ? 0 : _line) * 10;
    };
    const indexToString = index?.toString();
    /* Store Custom Code - End */

    return (
        <StyledWrapper {...styles.wrapper} as="span" /* Starter Custom Code */>
            {showProductNumber && (
                <>
                    {/* Store Custom Code - Start */}
                    {displayLineNumber && (
                        <StyledWrapper {...styles.innerWrapper} as="span">
                            {showLabel ? (
                                <Typography {...styles.lineNumberLabelText}>
                                    {translate("{0}:", translate("Line"))}&nbsp;
                                </Typography>
                            ) : (
                                <VisuallyHidden>
                                    <Typography {...styles.lineNumberLabelText}>
                                        {translate("{0}:", translate("Line"))}
                                    </Typography>
                                </VisuallyHidden>
                            )}
                            <Typography {...styles.lineNumberValueText} data-test-selector="lineNumber">
                                {isLineNumberMultiplied ? cartLineNumber : lineToLineNumber(cartLineNumber)}
                            </Typography>
                        </StyledWrapper>
                    )}
                    {/* Store Custom Code - End */}
                    <StyledWrapper {...styles.innerWrapper} as="span" /* Starter Custom Code */>
                        {showLabel && (
                            <Typography {...styles.erpNumberLabelText}>
                                {translate("{0}:", translate("Item #")) /* Store Custom Code */}
                            </Typography>
                        )}
                        {!showLabel && (
                            <VisuallyHidden>
                                <Typography {...styles.erpNumberLabelText}>
                                    {translate("{0}:", translate("Item #")) /* Store Custom Code */}
                                </Typography>
                            </VisuallyHidden>
                        )}
                        <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                            {productNumber}
                        </Typography>
                    </StyledWrapper>
                </>
            )}
            {displayCustomerNumber && (
                <StyledWrapper {...styles.innerWrapper} as="span" /* Starter Custom Code */>
                    {showLabel && (
                        <Typography {...styles.customerNameLabelText}>
                            {translate("{0}:", translate("Customer #")) /* Store Custom Code */}
                        </Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.customerNameLabelText}>
                                {translate("{0}:", translate("Customer #")) /* Store Custom Code */}
                            </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.customerNameValueText} data-test-selector="customerProductNumber">
                        {customerProductNumber}
                    </Typography>
                    {/* Store Custom Code - Start */}
                    {Boolean(additionalCustomerProductNumberCount) && (
                        <Typography {...styles.count}>+{additionalCustomerProductNumberCount}</Typography>
                    )}
                    {/* Store Custom Code - End */}
                </StyledWrapper>
            )}
            {displayManufacturerNumber && (
                <StyledWrapper {...styles.innerWrapper} as="span" /* Starter Custom Code */>
                    {showLabel && (
                        <Typography {...styles.manufacturerItemLabelText}>
                            {translate("{0}:", translate("MFG #")) /* Starter Custom Code */}
                        </Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.manufacturerItemLabelText}>
                                {translate("{0}:", translate("MFG #")) /* Starter Custom Code */}
                            </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.manufacturerItemValueText} data-test-selector="manufacturerItem">
                        {manufacturerItem}
                    </Typography>
                </StyledWrapper>
            )}
            {/* Store Custom Code - Start */}
            {showAdditionalInfo && <ProductAdditionalInfos pageType={pageType} pricing={pricing} />}
            {/* Store Custom Code - End */}
        </StyledWrapper>
    );
};

export default ProductPartNumbers;
